import { createContext, useContext, useEffect } from 'react'
import { Outlet, useParams } from 'react-router'
import { useHousehold } from '@/api/hooks'
import type { Household } from '@/api/types'
import { AppShell } from '@/components/layout/app-shell'
import { Loading, QueryError } from '@/components/ui/states'
import { rememberHousehold, forgetHousehold, recallHousehold } from '@/lib/household-memory'
import { ApiError } from '@/api/errors'

const HouseholdContext = createContext<Household | null>(null)

export function useCurrentHousehold(): Household {
  const household = useContext(HouseholdContext)
  if (!household) throw new Error('useCurrentHousehold outside HouseholdLayout')
  return household
}

/** Loads the household in the URL; 404 for foreign/unknown ones (no existence leak). */
export function HouseholdLayout() {
  const { householdId = '' } = useParams()
  const household = useHousehold(householdId)

  useEffect(() => {
    if (household.data) rememberHousehold(household.data.id)
    else if (household.error instanceof ApiError && household.error.status === 404 && recallHousehold() === householdId) forgetHousehold()
  }, [household.data, household.error, householdId])

  return (
    <AppShell householdId={householdId}>
      {household.isPending ? (
        <Loading />
      ) : household.isError ? (
        <QueryError error={household.error} onRetry={() => void household.refetch()} />
      ) : (
        <HouseholdContext.Provider value={household.data}>
          <Outlet />
        </HouseholdContext.Provider>
      )}
    </AppShell>
  )
}
